import { Checkbox, Paper, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as commonApi from '../../api/app/commonApi';
import * as monitoredCounterApi from '../../api/app/monitoredCounterApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { DataTable } from '../../components/core/DataTable/DataTable';
import { HeaderCell } from '../../components/core/DataTable/dataTable.types';
import { Loader } from '../../components/core/Loader/Loader';
import { useAuth } from '../../contexts/authWindows';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { MonitoredCounter } from '../../types/app/monitoredCounter.types';
import { UserRoles } from '../../types/core/auth.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  parentId: number;
}

export function MonitoredCounterList({ parentId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();
  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);
  const { data, isLoading, isError } = useQuery<MonitoredCounter[]>('monitoredCounters', () =>
    monitoredCounterApi.getMonitoredCountersByDep(parentId),
  );
  const { mutate: mutateDeleteMonitoredCounter } = useMutation(
    monitoredCounterApi.deleteMonitoredCounter,
  );

  const headerCells: HeaderCell[] = [
    {
      title: 'Main',
      key: 'main',
      cellRenderer: (e) => {
        return <Checkbox disabled checked={e.main} />;
      },
    },
    { title: 'OPC Client', key: 'opcClientInstanceName' },
    { title: 'Name', key: 'name' },
    { title: 'Influx value name', key: 'influxValueName' },
    { title: 'NodeId', key: 'nodeId' },
    {
      title: 'Multiple with hydra partitioning',
      key: 'useMultipleWithHydraPartitioning',
      cellRenderer: (e) => {
        return <Checkbox disabled checked={e.useMultipleWithHydraPartitioning} />;
      },
    },
    { title: 'Updated', key: 'updated', dataType: 'Date' },
  ];

  const handleOnCreate = () =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_COUNTER_CREATE}`);

  const handleOnEdit = (id: number) =>
    navigate(`${paths.ASSETS_LIST}/${parentId}${paths.MONITORED_COUNTER_DETAIL}/${id}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteMonitoredCounter(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.MONITORED_COUNTER_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['monitoredCounters']);
        queryClient.invalidateQueries(['assetTreeView']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data || isUsedHydra === undefined) return <Loader />;
  return (
    <>
      {/* <PageHeader
        title={dictionary.MONITORED_COUNTER}
        buttons={!isReadOnly ? headerButtons : null}
        headlineVariant={'h5'}
      /> */}
      <TableContainer component={Paper}>
        <DataTable
          headerCells={
            isUsedHydra
              ? headerCells
              : headerCells.filter(
                  (cell: HeaderCell) => cell.key !== 'useMultipleWithHydraPartitioning',
                )
          }
          items={data}
          onDeleteClick={handleOnDelete}
          onCreateClick={handleOnCreate}
          onEditClick={isReadOnly ? undefined : handleOnEdit}
          isReadOnly={isReadOnly}
        />
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
